import { Link } from "react-router-dom";
import { Phone, MapPin, ChevronRight, Trees, Fence, Mountain, Flower } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOMeta from "@/components/SEOMeta";
import LocalBusinessSchema from "@/components/LocalBusinessSchema";
import FAQ from "@/components/FAQ";

const faqData = [
  {
    question: "How long does a typical landscaping project take?",
    answer: "Timeline varies by project scope. Simple sod installation takes 1-2 days, while full landscape designs with hardscaping can take 1-2 weeks. We provide detailed timelines during consultation."
  },
  {
    question: "Do you provide landscape design services?",
    answer: "Yes, we offer full design-build services. Our team will work with you to create a custom landscape plan that fits your vision, budget, and property requirements."
  },
  {
    question: "What's the best time of year for landscaping?",
    answer: "Spring and fall are ideal for most landscaping work in Ontario. However, hardscaping can be done throughout warmer months, and we can schedule projects year-round based on the scope."
  },
  {
    question: "Do you offer maintenance services?",
    answer: "Yes, we provide ongoing maintenance packages including lawn care, seasonal planting, trimming, and hardscape maintenance to keep your landscape looking its best."
  }
];

const landscapingServices = [
  {
    title: "Lawn Installation & Sod",
    description: "Professional sod installation with proper grading and soil preparation for lush, healthy lawns.",
    icon: Trees,
    features: ["Site preparation", "Topsoil delivery", "Professional sod laying", "Irrigation setup"]
  },
  {
    title: "Patios & Hardscaping",
    description: "Custom patios, walkways, and retaining walls built with stone, pavers, and concrete.",
    icon: Mountain,
    features: ["Interlocking pavers", "Natural stone", "Stamped concrete", "Retaining walls"]
  },
  {
    title: "Garden Design & Planting",
    description: "Custom garden beds with native plantings, perennials, and seasonal color.",
    icon: Flower,
    features: ["Plant selection", "Bed preparation", "Mulching", "Seasonal rotation"]
  },
  {
    title: "Fencing & Privacy Screens",
    description: "Wooden and vinyl fencing installation for privacy, security, and aesthetic appeal.",
    icon: Fence,
    features: ["Privacy fencing", "Decorative fencing", "Gates & latches", "Fence repair"]
  }
];

const serviceAreas = [
  { name: "Niagara Region", link: "/locations" },
  { name: "Hamilton", link: "/locations/hamilton" },
  { name: "Toronto & GTA", link: "/locations/toronto" },
  { name: "Mississauga", link: "/locations/mississauga" }
];

export default function LandscapingServices() {
  return (
    <>
      <SEOMeta
        title="Landscaping & Hardscaping Contractor | Niagara • Hamilton • GTA"
        description="Full-service landscaping and hardscaping for lawns, patios and retaining walls in Niagara, Hamilton & the GTA. Design, build and maintain."
        keywords="landscaping contractor Niagara, Hamilton landscape design, GTA landscaper, sod installation, patios, retaining walls, gardens"
        canonicalUrl="https://blueknightcontracting.ca/services/landscaping"
      />
      <LocalBusinessSchema
        locationName="Landscaping Services"
        addressLocality="Niagara Falls"
        addressRegion="ON"
        description="Professional landscaping and hardscaping services across Ontario"
        services={["Lawn Installation", "Patio Construction", "Garden Design", "Retaining Walls", "Hardscaping"]}
      />
      <Header />
      <main className="min-h-screen bg-gradient-to-b from-background to-muted/20">
        {/* Hero Section */}
        <section className="relative py-20 px-4 bg-gradient-to-br from-primary/10 via-background to-accent/10">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              Landscaping & Hardscaping Services
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Transform your outdoor space with professional landscaping serving Niagara, Hamilton & the GTA
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button asChild size="lg" className="gap-2">
                <Link to="/contact">
                  Get Free Quote <ChevronRight className="h-5 w-5" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="gap-2">
                <a href="tel:+12899370797">
                  <Phone className="h-5 w-5" /> (289) 937-0797
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-16 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Landscaping Services</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                From concept to completion, we create outdoor spaces that enhance your property value and enjoyment
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {landscapingServices.map((service, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <service.icon className="h-12 w-12 text-primary mb-4" />
                    <CardTitle>{service.title}</CardTitle>
                    <CardDescription>{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <ChevronRight className="h-4 w-4 text-primary" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Why Choose Blue Knight?</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <Card>
                <CardHeader>
                  <CardTitle>Design-Build Expertise</CardTitle>
                  <CardDescription>
                    From initial concept to final planting, we handle every aspect of your landscape transformation
                  </CardDescription>
                </CardHeader>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Quality Materials</CardTitle>
                  <CardDescription>
                    We source premium sod, plants, stone, and pavers to ensure lasting beauty and durability
                  </CardDescription>
                </CardHeader>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Ongoing Support</CardTitle>
                  <CardDescription>
                    Maintenance packages available to keep your landscape healthy and beautiful year-round
                  </CardDescription>
                </CardHeader>
              </Card>
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-16 px-4">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Areas We Serve</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {serviceAreas.map((area, index) => (
                <Link key={index} to={area.link}>
                  <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                    <CardHeader>
                      <MapPin className="h-8 w-8 text-primary mb-2" />
                      <CardTitle className="text-lg">{area.name}</CardTitle>
                    </CardHeader>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Frequently Asked Questions</h2>
            <FAQ faqs={faqData} />
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Card className="bg-gradient-to-br from-primary/10 to-accent/10">
              <CardHeader>
                <CardTitle className="text-3xl mb-4">Ready to Transform Your Outdoor Space?</CardTitle>
                <CardDescription className="text-lg mb-6">
                  Contact us today for a free consultation and quote on your landscaping project
                </CardDescription>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button asChild size="lg">
                    <Link to="/contact">Get Free Quote</Link>
                  </Button>
                  <Button asChild size="lg" variant="outline">
                    <a href="tel:+12899370797">(289) 937-0797</a>
                  </Button>
                </div>
              </CardHeader>
            </Card>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
